// eslint-disable-next-line @altermaind/you-shall-not-pass
import { ComponentProps, FC, useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslations } from '@altermaind/core/i18n';
import { createGeneralCall } from '@altermaind/core/requests-status-management';
import { Box, Button, Card, Input, Svg, Typography } from '@altermaind/web/ui';
import { useBreakingPoints } from '@/_shared/hooks/useBreakpoint';
import { useDebounce } from '@/_shared/hooks/useDebounce';
import { useDialog } from '@/_shared/hooks/useDialog';
import { useNotification } from '@/_shared/hooks/useNotification';
import { useResponsiveStyle } from '@/_shared/hooks/useResponsiveStyle';
import { ContentType, Filter, Sort } from '@/swagger/constants/contents-orchestrator-enums';
import { FilterBadge } from './FilterBadge';
import { FilterBy } from './FilterBy';
import { FilterByTag } from './FilterByTag';
import { OrderBy } from './OrderBy';

type RequiredSetFiltersAndOrderPayload = {
  payload: {
    search?: string;
    tag?: string;
    sortBy?: `${Sort}`;
    filters?: `${Filter}`[];
    filterByTypes?: `${ContentType}`[];
  };
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type AnyType = any;

type CreateGeneralCallMustHaveFullRequestSelector = ReturnType<
  typeof createGeneralCall<
    AnyType,
    AnyType,
    AnyType,
    { [key: string]: AnyType; fullRequest: RequiredSetFiltersAndOrderPayload }
  >
>;

type Props = {
  title?: string;
  titleProps?: ComponentProps<typeof Typography>;
  showFiltersComponent?: boolean;
  onlyFilterByStateFilter?: boolean;
  call: CreateGeneralCallMustHaveFullRequestSelector;
  hideStatusFilter?: boolean;
  hideSearch?: boolean;
  hideTags?: boolean;
  hideOrderFilter?: boolean;
};

export const FilterContents: FC<Props> = ({
  title,
  titleProps,
  showFiltersComponent,
  onlyFilterByStateFilter,
  call,
  hideStatusFilter = false,
  hideSearch = false,
  hideTags = false,
  hideOrderFilter = false,
}) => {
  const t = useTranslations('trainingSoftSkills.filtersAndOrderContent');
  const sharedT = useTranslations();
  const { notify } = useNotification();
  const [searchValue, setSearchValue] = useState('');

  const {
    execute: executeCall,
    fullRequest: { payload: filters },
  } = call.hook();

  const { genericResponsiveValue } = useResponsiveStyle();
  const { isMobile } = useBreakingPoints();

  const {
    isOpen: isOpenFilter,
    closeDialog: closeDialogFilter,
    openDialog: openDialogFilter,
  } = useDialog('filters');

  const {
    isOpen: isOpenOrderBy,
    closeDialog: closeDialogOrderBy,
    openDialog: openDialogOrderBy,
  } = useDialog('orderBy');

  const activeFiltersCount = useMemo(() => {
    return (filters?.filters?.length || 0) + (filters?.filterByTypes?.length || 0);
  }, [filters]);

  const toggleFilters = useCallback(() => {
    if (isOpenFilter) {
      closeDialogFilter();
    } else {
      closeDialogOrderBy();
      openDialogFilter();
    }
  }, [isOpenFilter, closeDialogFilter, closeDialogOrderBy, openDialogFilter]);

  const toggleOrderBy = useCallback(() => {
    if (isOpenOrderBy) {
      closeDialogOrderBy();
    } else {
      closeDialogFilter();
      openDialogOrderBy();
    }
  }, [isOpenOrderBy, closeDialogOrderBy, closeDialogFilter, openDialogOrderBy]);

  const onSubmit = useCallback(
    ({
      search,
      tag,
      filterByTypes,
      filters: filterByState,
      sortBy,
    }: RequiredSetFiltersAndOrderPayload['payload']) => {
      const tagToUse = tag === 'all' ? undefined : tag || filters?.tag;
      executeCall({
        ...filters,
        search: search !== undefined ? search : filters?.search,
        tag: tagToUse,
        sortBy: sortBy || filters?.sortBy,
        filters: filterByState || filters?.filters,
        filterByTypes: filterByTypes || filters?.filterByTypes,
        start: 0,
        successSaga: () => {
          closeDialogFilter();
          closeDialogOrderBy();
        },
        errorSaga: () => {
          notify(sharedT('errors.genericApiError'), 'error');
        },
      });
    },
    [executeCall, filters, notify, sharedT, closeDialogFilter, closeDialogOrderBy],
  );

  const debouncedSearch = useDebounce((value: string) => {
    onSubmit({ search: value });
  }, 400);

  useEffect(() => {
    if (!isOpenFilter && !isOpenOrderBy) return;
    const handleClick = (event: MouseEvent) => {
      const filterBox = document.getElementById('filters-contains-filter-box');
      const orderByBox = document.getElementById('filters-contains-orderby-box');
      if (
        filterBox &&
        !(filterBox.contains(event.target as Node) || orderByBox?.contains(event.target as Node))
      ) {
        closeDialogFilter();
        closeDialogOrderBy();
      }
    };

    document.addEventListener('mousedown', handleClick);
    // eslint-disable-next-line consistent-return
    return () => {
      document.removeEventListener('mousedown', handleClick);
    };
  }, [closeDialogFilter, closeDialogOrderBy, isOpenFilter, isOpenOrderBy]);

  return (
    <>
      <Box
        flexDirection={genericResponsiveValue({
          breakPoints: { desktop: 'row' },
          defaultValue: 'column',
        })}
        display='flex'
        justifyContent='space-between'
        alignItems={genericResponsiveValue({
          breakPoints: { desktop: 'center' },
          defaultValue: 'flex-end',
        })}
        mb='s'
      >
        {title ? (
          <Box
            display='flex'
            alignItems={genericResponsiveValue({
              breakPoints: { desktop: 'center' },
              defaultValue: 'flex-start',
            })}
            mb={genericResponsiveValue({
              breakPoints: { desktop: 'none' },
              defaultValue: 's',
            })}
            width={genericResponsiveValue({
              breakPoints: { desktop: 'auto' },
              defaultValue: '100%',
            })}
          >
            <Typography
              variant='h3'
              flex='1'
              // eslint-disable-next-line react/jsx-props-no-spreading
              {...titleProps}
            >
              {title}
            </Typography>
          </Box>
        ) : (
          <Box flex='1' />
        )}
        <Box
          display='flex'
          alignItems={genericResponsiveValue({
            breakPoints: { desktop: 'center' },
            defaultValue: 'flex-start',
          })}
          width={genericResponsiveValue({
            breakPoints: { desktop: 'auto' },
            defaultValue: '100%',
          })}
        >
          {!hideSearch && (
            <Input
              suffixSvg='search'
              inputTextStyle={{ pr: 'l' }}
              type='text'
              placeholder={t('search')}
              value={searchValue}
              onChange={e => {
                const { value } = e.target;
                const isValid = value === '' || /^[\p{L}\p{N}\s\-_]+$/u.test(value);
                const sanitized = value.replace(/[^\p{L}\p{N}\s\-_]/gu, '');
                setSearchValue(sanitized);
                if (isValid) {
                  debouncedSearch(sanitized);
                }
              }}
            />
          )}
          {(showFiltersComponent || onlyFilterByStateFilter) && (
            <Box position='relative' zIndex={1000} id='filters-contains-filter-box'>
              <Button
                ml='xs'
                onClick={toggleFilters}
                type='button'
                backgroundColor='grey/dark/background'
                position='relative'
              >
                <Svg name='filter' fill='primary/text' size='xs' />
                <Typography as='span' variant='p2/light' ml='xxxs'>
                  {t('filter')}
                </Typography>
                <FilterBadge count={activeFiltersCount} />
              </Button>
              <Card
                overflow='visible'
                display={isOpenFilter ? 'block' : 'none'}
                right='0'
                top='xxl'
                backgroundColor='grey/dark/background'
                position='absolute'
                width={isMobile ? '240px' : '300px'}
              >
                <FilterBy
                  appliedFilters={{
                    filters: filters?.filters,
                    filterByTypes: filters?.filterByTypes,
                  }}
                  onChangeFilters={onSubmit}
                  onlyFilterByStateFilter={onlyFilterByStateFilter}
                  hideStatusFilter={hideStatusFilter}
                />
              </Card>
            </Box>
          )}
          {!hideOrderFilter && (
            <Box position='relative' zIndex={1000} id='filters-contains-orderby-box'>
              <Button
                ml='xs'
                onClick={toggleOrderBy}
                type='button'
                backgroundColor='grey/dark/background'
                position='relative'
              >
                <Svg name='sortBy' fill='primary/text' size='xs' />
                <Typography as='span' variant='p2/light' ml='xxxs'>
                  {t('orderBy')}
                </Typography>
              </Button>
              <Card
                overflow='visible'
                display={isOpenOrderBy ? 'block' : 'none'}
                right='0'
                top='xxl'
                backgroundColor='grey/dark/background'
                position='absolute'
                width={isMobile ? '240px' : '300px'}
              >
                <OrderBy
                  appliedOrder={{
                    sortBy: filters?.sortBy,
                  }}
                  onChangeOrder={onSubmit}
                />
              </Card>
            </Box>
          )}
        </Box>
      </Box>
      {!hideTags && <FilterByTag onChangeTag={onSubmit} />}
    </>
  );
};
